/**
 * Zustand store for authentication state
 * 
 * This store manages user authentication state to reduce re-renders
 * compared to using useState in the useAuth hook.
 */

import { create } from 'zustand';
import type { User } from '@/models';

interface AuthStore {
  user: User | null;
  loading: boolean;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  clearAuth: () => void;
}

/**
 * Auth store for managing authentication state
 * 
 * This reduces re-renders compared to useState in useAuth hook.
 * State is synced with sessionStorage for backward compatibility.
 */
export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  loading: true,
  setUser: (user) => {
    set({ user });
    // Also sync to sessionStorage for backward compatibility
    if (typeof window !== 'undefined') {
      if (user) {
        sessionStorage.setItem('user', JSON.stringify(user));
      } else {
        sessionStorage.removeItem('user');
      }
    }
  },
  setLoading: (loading) => set({ loading }),
  clearAuth: () => {
    set({ user: null, loading: false });
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('access_token');
    }
  },
}));

// Initialize from sessionStorage on store creation
if (typeof window !== 'undefined') {
  try {
    const storedUser = sessionStorage.getItem('user');
    if (storedUser) {
      useAuthStore.getState().setUser(JSON.parse(storedUser));
    }
    useAuthStore.getState().setLoading(false);
  } catch (error) {
    console.error('Failed to initialize auth store from sessionStorage:', error);
  }
}

